import { handleAnonId } from '../storage';
import { eraseAjsLocalStorage, setAjsLocalStorageByCompanyId } from './analytics-storage';
import { validScriptIntegrations } from '../tracking/integrations';
import middleware from './middleware';
import v3 from '../tracking/v3.calls';
import v4 from '../tracking/v4.calls';
import { isV3, isV4 } from '../tracking-version';
import debug from 'debug';

// Types
import type { IntegrationData } from '$lib/components/ConsentOverlay/types';

const debugLog = debug('global:analytics');

let loaded = false;

export const isAnalyticsLoaded = () => loaded;

export const loadAnalytics = ({
    companyId,
    callback,
    preventTracking,
    analyticsLink,
}: {
    companyId: string;
    callback: () => void;
    preventTracking: boolean;
    analyticsLink: string;
}) => {
    debugLog('-> loadAnalytics', companyId, preventTracking, analyticsLink);

    if (preventTracking) {
        callback();

        return;
    }

    eraseAjsLocalStorage();
    setAjsLocalStorageByCompanyId(companyId);

    window.analytics.load = () => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = analyticsLink;
        const firstElement = document.getElementsByTagName('script')[0];
        firstElement?.parentNode?.insertBefore(script, firstElement);

        script.onload = function () {
            callback();
        };
    };
    window.analytics.SNIPPET_VERSION = '4.1.0';
    window.analytics?.load();
};

export const loadedIntegrations: Record<string, boolean> = {};

export const activateIntegrations = (integrations: IntegrationData) => {
    const integrationNames = Object.keys(integrations);
    const notLoaded: IntegrationData = {};

    for (let i = 0; i < integrationNames.length; i++) {
        const integrationName = integrationNames[i];

        if (!loadedIntegrations[integrationName]) {
            notLoaded[integrationName] = integrations[integrationName];
        }
    }

    if (!Object.keys(notLoaded).length) {
        return;
    }

    if (!isAnalyticsLoaded() || !window.analytics.initialize) {
        // when preferences are set in the localstorage, this method is called immediately
        // even when analytics.js is not loaded yet - so we add a event listener
        // instead of calling the `initializeWithIntegrations` directly
        window.analytics.ready(() => {
            initializeWithIntegrations(integrations);
        });
    } else {
        initializeWithIntegrations(integrations);
    }
};

const initializeWithIntegrations = (integrations: IntegrationData) => {
    // adds all given integrations to analytics.js and loads them
    window.analytics.initialize(integrations);
    Object.keys(integrations).forEach((integrationName) => {
        loadedIntegrations[integrationName] = true;
    });

    const ps_queue = (window as any).ps_queue || [];
    while (ps_queue.length > 0) {
        const argumentList = ps_queue.shift();
        // can be identify, track, page
        const method = argumentList.shift();

        if (window.analytics[method]) {
            let contextIndex = -1;
            if (method === 'identify' || method === 'track') {
                contextIndex = 2;
            }
            if (method === 'page') {
                contextIndex = 3;
            }

            // only process known methods
            if (contextIndex > -1) {
                // deactivate perspective tracking for all events because
                // perspective tracking was already initialized and triggered
                argumentList[contextIndex].integrations = {
                    ...(argumentList[contextIndex].integrations || {}),
                    Perspective: false,
                };
                // this will do something like
                // window.analytics.track('event', {params}, { integrations: { Perspective: false }});
                // window.analytics.identify('anon_id', {traits}, { integrations: { Perspective: false }});
                // window.analytics.page(null, 'home', {params}, { integrations: { Perspective: false }});
                window.analytics[method](...argumentList);
            }
        }
    }
};

export const loadAnalyticsWrapper = ({
    companyId,
    integrations,
    preventTracking,
    analyticsLink,
}: {
    companyId: string;
    integrations: Record<string, boolean>;
    preventTracking: boolean;
    analyticsLink: string;
}) => {
    if (isAnalyticsLoaded()) {
        return;
    }

    const duplicate = JSON.parse(JSON.stringify(integrations));

    Object.keys(duplicate).forEach((displayName) => {
        if (validScriptIntegrations.indexOf(displayName) === -1) {
            delete duplicate[displayName];
        }

        loadedIntegrations[displayName] = true;
    });

    loaded = true;

    const callback = () => {
        window.analytics.reset();
        handleAnonId();

        // removes call by reference bindings
        if (window.analytics && window.analytics.initialize) {
            try {
                if (isV4()) {
                    window.analytics.use(window.PS_TRACKING);
                }
                window.analytics.addIntegrationMiddleware(middleware);
                window.analytics.initialize(duplicate);
            } catch (err) {
                console.warn('analytics init error', err);
                throw err;
            }

            // Loop through the interim analytics queue and reapply the calls to their
            // proper analytics.js method.
            while (window.analytics_queue.length > 0) {
                const item = window.analytics_queue.shift();
                const method = item.shift();

                if (window.analytics[method]) {
                    window.analytics[method](...item);
                }
            }

            // sets correct localstorage
            if (isV3()) {
                v3.identify();
            } else {
                v4.identify();
            }
        }
    };

    loadAnalytics({
        companyId,
        preventTracking,
        analyticsLink,
        callback,
    });
};

export default loadAnalyticsWrapper;
